import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Card, Flex, Result, Spin, Tag, Typography, Alert, Space } from 'antd';
import {
    ArrowLeftOutlined, VideoCameraOutlined, PauseCircleOutlined, ClockCircleOutlined, CoffeeOutlined,
    SmileOutlined, SettingOutlined, CalendarOutlined, LogoutOutlined, ArrowRightOutlined,
} from '@ant-design/icons';
import { PreJoin } from '@livekit/components-react';
import '@livekit/components-styles';
import { useMemberGathering, useServerClock } from '../../hooks/use_gathering';
import { useGatheringIdentity } from '../../hooks/use_gathering_identity';
import { flowPosition, parseServerTime, formatCountdown, stopFor } from '../../utils/gathering';
import { isGuestPerson } from '../../utils/guest_session';
import { EntityImage } from '../Entities/EntityImage';
import VideoCallRoom from '../Hangouts/VideoCallRoom';
import PieceBackdrop from '../Skargard/PieceBackdrop';
import Fold from '../Skargard/Fold';
import FlowTimeline, { stepLabels, StepKindIcon, StepCountdown } from './FlowTimeline';
import '../Hangouts/VideoCall.css';
import './Gathering.css';

const { Title, Text, Paragraph } = Typography;

// The countdown turns urgent for a step's last minute, and the room bar
// starts showing where you're headed next two minutes out.
const NEARLY_OVER_MS = 60 * 1000;
const NEXT_PREVIEW_MS = 2 * 60 * 1000;
// Faster than the member view's 15s: a pause, "next step" or extension from
// the host should reach people who are mid-call within a few seconds.
const POLL_MS = 5000;

/** "Common room" / "Round 2 · Room 3" / "Round 2" (not seated this round). */
function stopLabel(stop, labels) {
    if (!stop) return '';
    if (stop.kind === 'common') return 'Common room';
    return stop.room ? `${labels[stop.index]} · Room ${stop.room.index + 1}` : labels[stop.index];
}

/** The step's time left as a compact tag, for the in-call bar. */
function CountdownTag({ remainingMs, paused }) {
    const nearly = !paused && remainingMs < NEARLY_OVER_MS;
    return (
        <Tag
            className="gathering-flow-countdown"
            color={paused ? 'warning' : nearly ? 'error' : 'processing'}
            icon={paused ? <PauseCircleOutlined /> : <ClockCircleOutlined />}
        >
            {paused ? 'Paused · ' : ''}{formatCountdown(remainingMs)}{nearly ? ' left' : ''}
        </Tag>
    );
}

/** Who you'll be with in a breakout room. */
function RoomPeople({ room, myPersonId }) {
    if (!room?.people?.length) return null;
    return (
        <Flex wrap gap={6}>
            {room.people.map(p => (
                <Tag key={p.id} className="gathering-schedule-person" color={p.id === myPersonId ? 'blue' : undefined}>
                    <EntityImage entity={p} size={18} />
                    {p.name}{p.id === myPersonId ? ' (you)' : isGuestPerson(p) ? ' (guest)' : ''}
                </Tag>
            ))}
        </Flex>
    );
}

/** Full-screen backdrop + a way back, for every screen that isn't a call. */
function Stage({ onBack, children }) {
    return (
        <PieceBackdrop piece={Fold} followTheme>
            <div className="gathering-flow">
                <div className="gathering-flow-bar">
                    <Button type="text" icon={<ArrowLeftOutlined />} onClick={onBack}>Back to Gathering</Button>
                </div>
                <div className="gathering-flow-stage">{children}</div>
            </div>
        </PieceBackdrop>
    );
}

/**
 * /gathering/flow — follow the gathering live, in one place. The flow's
 * clock (flowPosition on the server's time) decides where each member should
 * be right now: the common room for a common step, their own room for a
 * breakout round (stopFor). Between rooms they land on a transition screen —
 * where they're headed, who with, the topic and the time left — and join
 * when they're ready; the first join sets up camera and microphone, later
 * ones reuse those choices. When a step ends the call is left automatically
 * and the next transition appears. A pause freezes the countdown and keeps
 * everyone where they are.
 *
 * Works the same for guests (useGatheringIdentity). The per-step join
 * buttons in MemberView and GatheringCall stay for anyone who'd rather hop
 * in by hand.
 */
function GatheringFlow() {
    const navigate = useNavigate();
    // At /app/:instanceId/flow this is an app gathering's flow
    // (sdk_apps/gathering.py); at /gathering/flow, the node's.
    const { instanceId = null } = useParams();
    const { name, isGuest } = useGatheringIdentity();
    const { data, loading, error, refresh } = useMemberGathering({ pollMs: POLL_MS, instanceId });
    // LiveKit PreJoin's result — camera, mic, display name — reused for every room.
    const [choices, setChoices] = useState(null);
    const [settingUp, setSettingUp] = useState(false);
    // The stop (stopFor(...).key) the member has chosen to join. Anything
    // else — the step ending, the host moving them — means the transition.
    const [joinedKey, setJoinedKey] = useState(null);

    const startedAt = parseServerTime(data?.flow_started_at);
    const pausedAt = parseServerTime(data?.flow_paused_at);
    const paused = startedAt != null && pausedAt != null;
    const now = useServerClock(data?.server_now, startedAt != null && !paused);
    const schedule = data?.schedule ?? [];
    const position = flowPosition(schedule, startedAt, paused ? pausedAt : now);
    const liveIndex = position && !position.finished ? position.index : -1;
    const labels = stepLabels(schedule);
    const commonRoom = data?.common_hangout_room_name;
    const stop = liveIndex >= 0 ? stopFor(schedule, liveIndex, commonRoom) : null;
    const next = liveIndex >= 0 ? stopFor(schedule, liveIndex + 1, commonRoom) : null;

    // The local clock crossing a step boundary is a guess until the server
    // agrees — the host may have just extended the step or skipped ahead —
    // so check straight away rather than waiting for the next poll.
    const boundary = useRef(liveIndex);
    useEffect(() => {
        if (boundary.current !== liveIndex) {
            boundary.current = liveIndex;
            refresh();
        }
    }, [liveIndex, refresh]);

    const back = () => navigate(instanceId ? `/app/${instanceId}` : '/gathering');
    const identity = (isGuest ? `${name} (guest)` : name) || 'anonymous';

    if (loading) {
        return <Stage onBack={back}><Spin size="large" /></Stage>;
    }
    if (error) {
        return (
            <Stage onBack={back}>
                <Result status="error" title="Couldn't load the gathering" subTitle={error}
                    extra={<Button onClick={refresh}>Try again</Button>} />
            </Stage>
        );
    }
    if (!data) return null;

    if (data.status !== 'open') {
        return (
            <Stage onBack={back}>
                <Card className="gathering-flow-card">
                    {data.status === 'waiting' ? (
                        <Result
                            icon={<CoffeeOutlined style={{ color: 'var(--vellby-sky-500)' }} />}
                            title="You're in the waiting room"
                            subTitle="The gathering is about to begin. Stay here — your first room appears by itself."
                            extra={<Spin size="small" />}
                        />
                    ) : (
                        <Result
                            icon={<CalendarOutlined style={{ color: 'var(--text-tertiary)' }} />}
                            title="No gathering right now"
                            subTitle={data.message || 'Check back later — this page updates by itself when one opens.'}
                        />
                    )}
                </Card>
            </Stage>
        );
    }

    if (startedAt == null) {
        const first = stopFor(schedule, 0, commonRoom);
        return (
            <Stage onBack={back}>
                <Card className="gathering-flow-card">
                    <Flex vertical gap={16}>
                        <Result
                            icon={<CoffeeOutlined style={{ color: 'var(--vellby-sky-500)' }} />}
                            title="The gathering hasn't started yet"
                            subTitle="Stay on this page — when the host starts, you'll be taken to your first room."
                            extra={<Spin size="small" />}
                        />
                        {first && (
                            <Text type="secondary" style={{ textAlign: 'center' }}>
                                First up: <Text strong>{stopLabel(first, labels)}</Text>
                            </Text>
                        )}
                        <FlowTimeline steps={schedule} position={null} startedAt={null} />
                    </Flex>
                </Card>
            </Stage>
        );
    }

    if (position.finished) {
        return (
            <Stage onBack={back}>
                <Card className="gathering-flow-card">
                    <Result
                        icon={<SmileOutlined style={{ color: 'var(--vellby-sky-500)' }} />}
                        title="That's a wrap"
                        subTitle="The gathering is over — thanks for taking part."
                        extra={<Button type="primary" onClick={back}>Back to Gathering</Button>}
                    />
                </Card>
            </Stage>
        );
    }

    const label = stopLabel(stop, labels);
    const inRoom = !!(stop.roomName && choices && joinedKey === stop.key);

    if (inRoom) {
        const showNext = next && !paused && position.remainingMs < NEXT_PREVIEW_MS;
        return (
            <div className="gathering-flow is-live">
                <div className="gathering-flow-bar">
                    <Space size={8} wrap>
                        <StepKindIcon kind={stop.kind} />
                        <Text strong>{label}</Text>
                        {stop.room?.topic && <Text type="secondary" className="gathering-flow-topic">· {stop.room.topic}</Text>}
                    </Space>
                    <Space size={8} wrap>
                        {showNext && (
                            <Tag icon={<ArrowRightOutlined />}>Next: {next.key === stop.key ? 'staying here' : stopLabel(next, labels)}</Tag>
                        )}
                        <CountdownTag remainingMs={position.remainingMs} paused={paused} />
                        <Button size="small" icon={<LogoutOutlined />} onClick={() => setJoinedKey(null)}>Leave room</Button>
                    </Space>
                </div>
                <div className="gathering-flow-call">
                    <VideoCallRoom
                        key={stop.roomName}
                        roomName={stop.roomName}
                        participantIdentity={choices.username || identity}
                        entityId={null}
                        onLeave={() => setJoinedKey(null)}
                        userChoices={choices}
                        fullscreen
                    />
                </div>
            </div>
        );
    }

    // The transition: where to go now, and a button to go there.
    const heading = stop.kind === 'common' ? 'Head to the common room'
        : stop.room ? `Head to Room ${stop.room.index + 1}` : labels[liveIndex];
    const previous = liveIndex > 0 ? stopFor(schedule, liveIndex - 1, commonRoom) : null;
    const justMoved = previous && previous.key !== stop.key;
    const nearlyOver = !paused && position.remainingMs < NEARLY_OVER_MS;

    const action = () => {
        if (stop.kind === 'breakout' && !stop.room) {
            return (
                <Alert
                    type="info"
                    showIcon
                    title="You're not in a room this round"
                    description="The host can add you — this page updates by itself. You'll be taken on at the next step."
                />
            );
        }
        if (!stop.roomName) {
            return (
                <Flex align="center" gap={10} justify="center">
                    <Spin size="small" />
                    <Text type="secondary">The video room is getting ready…</Text>
                </Flex>
            );
        }
        if (!choices || settingUp) {
            return (
                <Flex vertical gap={8} align="center">
                    <Text type="secondary">Check your camera and microphone, then join.</Text>
                    <div className="prejoin-wrapper" data-lk-theme="default">
                        <PreJoin
                            defaults={{
                                username: choices?.username || identity,
                                videoEnabled: choices?.videoEnabled ?? true,
                                audioEnabled: choices?.audioEnabled ?? true,
                            }}
                            joinLabel={`Join ${stop.kind === 'common' ? 'the common room' : `Room ${stop.room.index + 1}`}`}
                            onSubmit={c => { setChoices(c); setSettingUp(false); setJoinedKey(stop.key); }}
                            onError={err => console.error('PreJoin error:', err)}
                            persistUserChoices
                        />
                    </div>
                </Flex>
            );
        }
        return (
            <Flex vertical gap={6} align="center">
                <Button type="primary" size="large" icon={<VideoCameraOutlined />} onClick={() => setJoinedKey(stop.key)}>
                    {stop.kind === 'common' ? 'Join the common room' : `Join Room ${stop.room.index + 1}`}
                </Button>
                <Button type="link" size="small" icon={<SettingOutlined />} onClick={() => setSettingUp(true)}>
                    Camera and microphone
                </Button>
            </Flex>
        );
    };

    return (
        <Stage onBack={back}>
            <Card className="gathering-flow-card">
                <Flex vertical gap={16}>
                    <Flex vertical gap={4} align="center" style={{ textAlign: 'center' }}>
                        <Text type="secondary">
                            {justMoved ? `${stopLabel(previous, labels)} is over — time to move.`
                                : liveIndex === 0 ? 'The gathering has started.' : 'You’re between rooms.'}
                        </Text>
                        <Title level={3} style={{ margin: 0 }}>{heading}</Title>
                        <Tag icon={<StepKindIcon kind={stop.kind} />} style={{ margin: 0 }}>{label}</Tag>
                    </Flex>

                    <Flex justify="center">
                        <StepCountdown label={labels[liveIndex]} remainingMs={position.remainingMs} paused={paused} />
                    </Flex>
                    {paused && (
                        <Alert type="warning" showIcon title="The host has paused the gathering" description="The clock is stopped — you can still join." />
                    )}
                    {nearlyOver && (
                        <Text type="warning" style={{ textAlign: 'center' }}>
                            This step is nearly over{next ? ` — ${stopLabel(next, labels)} is next` : ''}.
                        </Text>
                    )}

                    {stop.room?.topic && <Alert type="info" title="Your topic" description={stop.room.topic} />}
                    {stop.room && (
                        <Flex vertical gap={6}>
                            <Text type="secondary" style={{ fontSize: 'var(--fs-sm)' }}>In this room</Text>
                            <RoomPeople room={stop.room} myPersonId={data.my_person_id} />
                        </Flex>
                    )}

                    {action()}

                    {next && (
                        <Paragraph type="secondary" style={{ textAlign: 'center', margin: 0, fontSize: 'var(--fs-sm)' }}>
                            After this: {next.key === stop.key ? 'staying here' : stopLabel(next, labels)}
                        </Paragraph>
                    )}
                </Flex>
            </Card>
        </Stage>
    );
}

export default GatheringFlow;
